import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/Form.css";
import logo from "../../images/logo.png";

const PspDisclosurePage = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();

  const loggedInUserId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";

  const effectiveUserId = isAdmin && paramId ? paramId : loggedInUserId;
  const isSlidePage = location.pathname.startsWith("/slide/");

  const [username, setUsername] = useState("");
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");

  // 🔑 MUST MATCH nwt_tasks
  const TASK_ID = 30;
  const TASK_KEY = "psp_disclosure";

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUsername(`${res.data.first_name} ${res.data.last_name}`);
      } catch (err) {
        console.error("Error fetching user name", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const idKey = String(TASK_ID);
        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[TASK_KEY] ??
          "not_completed";

        const signed = status === "review" || status === "completed";

        if (signed) {
          setIsSigned(true);
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[TASK_KEY]
          );
        }
      } catch (err) {
        console.error("Error checking PSP status", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [API_BASE, effectiveUserId, isAdmin]);

  return (
    <>
      {/* 🔝 Top Bar */}
      <div className="d-flex justify-content-between align-items-center pt-3 px-3">
        {!isSlidePage && (
          <span style={{ fontSize: 18, fontWeight: 500 }}>
            Consent to Pre-Employment Screening Program
          </span>
        )}
        <div className="ms-auto">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            {isSlidePage ? "Close" : "Return"}
          </button>
        </div>
      </div>

      {/* 🧾 Main Content */}
      <div
        className={`form-wrapper my-4 ${isSlidePage ? "slide-narrow" : "px-4"}`}
        style={{ fontSize: 15, lineHeight: 1.6 }}
      >
        {/* Logo Header (non-slide) */}
        {!isSlidePage && (
          <div className="row align-items-center mb-4">
            <div className="col-3">
              <img src={logo} alt="Logo" style={{ width: 150 }} />
            </div>
            <div className="col-6 text-center">
              <h2 className="m-0">
                Consent to Pre-Employment Screening Program
              </h2>
            </div>
            <CompanyAddress />
          </div>
        )}

        {/* Slide Header */}
        {isSlidePage && (
          <div
            className="px-3 pb-2 mb-3"
            style={{ borderBottom: "1px solid #ccc" }}
          >
            <h4 style={{ fontSize: 18, fontWeight: 500 }}>
              Review and Sign Consent – PSP Disclosure
            </h4>
          </div>
        )}

        {/* PSP CONTENT */}
        <p>
          I understand that the Pre-Employment Screening Program (PSP) provides
          access to my commercial driving safety history as maintained by the
          Federal Motor Carrier Safety Administration (FMCSA).
        </p>

        <p>
          I authorize New-Way Trucking Ltd to obtain my PSP record for
          employment screening purposes. This record may include crash data and
          roadside inspection history for the legally permitted time periods.
        </p>

        <p>
          I understand that my consent is required and that this authorization
          is valid for a single PSP report request.
        </p>

        <hr className="my-5" />

        {/* Name / Date */}
        <div className="row mb-4">
          <div className="col">
            <label className="form-label">Employee Name</label>
            <input className="form-control" value={username} readOnly />
          </div>
          <div className="col">
            <label className="form-label">Date</label>
            <input
              className="form-control"
              value={
                isSigned && signedDate
                  ? new Date(signedDate).toLocaleDateString()
                  : new Date().toLocaleDateString()
              }
              readOnly
            />
          </div>
        </div>

        {/* Signature */}
        {effectiveUserId && (
          <>
            <SignatureManager
              taskName={TASK_KEY}
              taskId={TASK_ID}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName={TASK_KEY}
              taskId={TASK_ID}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default PspDisclosurePage;
